/**
 * Haley Gentleman Showroom 2026
 * Web Component — extends theme Component base class
 */
import { Component } from '@theme/component';

class HaleyGentlemanComponent extends Component {
  /** @type {boolean} */
  #scrollTicking = false;

  /** @type {string} */
  #waBaseMsg = '';

  /** @type {Array<Object>} */
  #variants = [];

  connectedCallback() {
    super.connectedCallback();
    this.#initReveal();
    this.#initPill();
    this.#initBattery();
    this.#initCounters();
    this.#initLayers();
    this.#initConfigurator();
    this.#initVariantPicker();
    this.#initHeroCarousel();
    this.#initAnalytics();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#onScroll);
  }

  // ─── 1. Reveal Observer ───────────────────────────────────────────
  #initReveal() {
    const els = this.querySelectorAll('[data-hg-reveal]');
    if (!els.length) return;

    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('hg-visible');
          io.unobserve(e.target);
        }
      }
    }, { threshold: 0.15 });

    for (const el of els) io.observe(el);
  }

  // ─── 2. WhatsApp Floating Pill ────────────────────────────────────
  #initPill() {
    const pill = this.refs.whatsappPill;
    const hero = this.refs.hero;
    if (!pill || !hero) return;

    const io = new IntersectionObserver(([entry]) => {
      pill.classList.toggle('hg-visible', !entry.isIntersecting);
    }, { threshold: 0 });

    io.observe(hero);
  }

  // ─── 3. Battery Widget ────────────────────────────────────────────
  #onScroll = () => {
    if (this.#scrollTicking) return;
    this.#scrollTicking = true;

    requestAnimationFrame(() => {
      const fill = this.refs.batteryFill;
      const text = this.refs.batteryText;
      if (!fill || !text) { this.#scrollTicking = false; return; }

      const pct = Math.min(100, Math.max(0,
        (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
      ));

      fill.style.width = `${pct}%`;

      if (pct >= 99.5) {
        text.textContent = 'READY FOR REST';
        text.classList.remove('typing-cursor');
        fill.style.background = '#FF9DA7';
        fill.style.boxShadow = '0 8px 18px rgba(255, 157, 167, 0.28)';
      } else {
        text.textContent = `${Math.round(pct)}% READY...`;
        text.classList.add('typing-cursor');
        fill.style.background = 'var(--haley-accent-dark)';
        fill.style.boxShadow = '0 8px 18px rgba(122, 98, 83, 0.22)';
      }
      this.#scrollTicking = false;
    });
  };

  #initBattery() {
    if (!this.refs.batteryFill || !this.refs.batteryText) return;
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  // ─── 4. Number Counters ───────────────────────────────────────────
  #initCounters() {
    const counters = this.querySelectorAll('[data-hg-count]');
    if (!counters.length) return;

    const animate = (el) => {
      const target = parseInt(el.getAttribute('data-hg-count'), 10);
      const suffix = el.getAttribute('data-hg-suffix') ?? '';
      const prefix = el.getAttribute('data-hg-prefix') ?? '';
      const duration = 1500;
      const start = performance.now();

      // Look for a specific number child to update to avoid wiping out labels
      const numEl = el.querySelector('.hg-stat__number');

      const step = (now) => {
        let t = Math.min((now - start) / duration, 1);
        t = 1 - (1 - t) ** 2; // ease-out quad
        const currentVal = Math.round(target * t).toLocaleString();

        if (numEl) {
          numEl.textContent = currentVal;
        } else {
          el.textContent = prefix + currentVal + suffix;
        }

        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          animate(e.target);
          io.unobserve(e.target);
        }
      }
    }, { threshold: 0.3 });

    for (const el of counters) io.observe(el);
  }

  // ─── 5. Layer Switcher ────────────────────────────────────────────
  #initLayers() {
    const sidebar = this.refs.layerSidebar;
    const descPane = this.refs.layerDesc;
    const visualPane = this.refs.layerVisual;
    const pointerLabel = this.refs.pointerLabel;
    if (!sidebar || !descPane) return;

    const switchTo = (num) => {
      const btn = sidebar.querySelector(`.hg-layer-btn[data-layer="${num}"]`);
      if (!btn) return;

      const title = btn.dataset.title;
      const meta = btn.dataset.meta;
      const desc = btn.dataset.desc;
      if (!title) return;

      // Update active button
      for (const b of sidebar.querySelectorAll('.hg-layer-btn')) {
        b.classList.toggle('active', parseInt(b.dataset.layer, 10) === num);
      }

      const render = () => {
        descPane.innerHTML =
          `<h3>${title}</h3>` +
          `<p class="hg-layer-meta">${meta}</p>` +
          `<p>${desc}</p>`;
        descPane.style.opacity = '1';

        visualPane?.setAttribute('data-layer', num);
        if (pointerLabel) pointerLabel.textContent = meta;
      };

      if (document.startViewTransition) {
        descPane.style.opacity = '0';
        document.startViewTransition(() => { render(); return Promise.resolve(); });
      } else {
        descPane.style.opacity = '0';
        setTimeout(render, 300);
      }
    };

    sidebar.addEventListener('click', (e) => {
      const btn = e.target.closest('.hg-layer-btn');
      if (!btn) return;
      const num = parseInt(btn.dataset.layer, 10);
      if (!isNaN(num)) switchTo(num);
    });
  }

  // ─── 6. Sleep Concern Configurator ────────────────────────────────
  #initConfigurator() {
    const configurator = this.refs.configurator;
    if (!configurator) return;

    const buttons = configurator.querySelectorAll('.hg-config-btn');
    const pillarCards = this.querySelectorAll('.hg-pillar-card');
    const whatsappLinks = this.refs.whatsappLinks ?? [];

    // Grab base message from first whatsapp link
    if (whatsappLinks.length) {
      try {
        this.#waBaseMsg = new URL(whatsappLinks[0].href).searchParams.get('text') ?? '';
      } catch { }
    }

    for (const btn of buttons) {
      btn.addEventListener('click', () => {
        // Toggle active state for current button
        btn.classList.toggle('active');

        // Get all currently active concerns
        const activeButtons = Array.from(buttons).filter(b => b.classList.contains('active'));
        const activeConcerns = activeButtons.map(b => b.dataset.concern);

        // Highlight all matching pillar cards
        for (const card of pillarCards) {
          card.classList.toggle('hg-highlighted', activeConcerns.includes(card.dataset.concern));
        }

        // Update WhatsApp links with all active concerns
        let newMsg = this.#waBaseMsg;
        if (activeButtons.length > 0) {
          const concernTexts = activeButtons.map(b => b.textContent.trim()).join('、');
          newMsg += (newMsg ? '\n' : '') + '我最關心：' + concernTexts;
        }

        for (const link of whatsappLinks) {
          try {
            const u = new URL(link.href);
            u.searchParams.set('text', newMsg);
            link.href = u.toString();
          } catch { }
        }
      });
    }
  }

  // ─── 7. Variant Picker ────────────────────────────────────────────
  #initVariantPicker() {
    const jsonEl = this.refs.productJson;
    if (!jsonEl) return;

    try {
      this.#variants = JSON.parse(jsonEl.textContent);
    } catch {
      return;
    }

    const pickers = this.refs.variantPickers ?? [];
    if (!pickers.length) return;

    const getSelectedOptions = (picker) => {
      const selected = {};
      for (const input of picker.querySelectorAll('input[type="radio"]:checked')) {
        selected[input.dataset.optionIndex] = input.value;
      }
      return selected;
    };

    const findVariant = (selected) => {
      const variant = this.#variants.find(v =>
        Object.entries(selected).every(([key, val]) => v.options?.[+key] === val)
      );
      if (!variant) console.warn('[HaleyGentleman] No variant match for', selected);
      return variant ?? null;
    };

    const formatMoney = (cents) => {
      const code = window.Shopify?.currency?.active ?? 'HKD';
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: code });
    };

    const syncPickers = (sourcePicker) => {
      const selected = getSelectedOptions(sourcePicker);
      for (const picker of pickers) {
        if (picker === sourcePicker) continue;
        for (const key in selected) {
          const radio = picker.querySelector(
            `input[data-option-index="${key}"][value="${CSS.escape(selected[key])}"]`
          );
          if (radio) radio.checked = true;
        }
        updatePickerUI(picker, selected);
      }
    };

    const updatePickerUI = (picker, selected) => {
      const variant = findVariant(selected);
      const form = picker.closest('form') ?? picker.closest('.hg-cart-form');

      // Update variant ID input
      const idInput = form?.querySelector('input[name="id"]');
      if (idInput && variant) idInput.value = variant.id;

      // Update active label classes
      for (const input of picker.querySelectorAll('input[type="radio"]')) {
        input.closest('.hg-variant-btn')?.classList.toggle('active', input.checked);
      }

      if (!variant) return;

      // Update all price displays (BUG FIX: was .hg-current-price, correct is .hg-price--current)
      for (const el of this.querySelectorAll('.hg-price--current')) {
        el.textContent = formatMoney(variant.price);
      }

      // Update compare-at-price + discount badge from variant (not product)
      const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      for (const el of this.querySelectorAll('[id^="hg-compare-price"], .hg-price--compare')) {
        if (hasCompare) {
          el.textContent = formatMoney(variant.compare_at_price);
          el.style.display = '';
        } else {
          el.textContent = '';
          el.style.display = 'none';
        }
      }
      for (const el of this.querySelectorAll('[id^="hg-discount"], .hg-price--discount')) {
        if (hasCompare) {
          const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          el.textContent = '-' + pct + '%';
          el.style.display = '';
        } else {
          el.textContent = '';
          el.style.display = 'none';
        }
      }

      // Update all cart buttons
      for (const btn of this.querySelectorAll('.hg-btn--cart')) {
        const textEl = btn.querySelector('.hg-cart-text, [id^="hg-add-to-cart-text"]');
        if (variant.available) {
          btn.disabled = false;
          btn.style.opacity = '';
          btn.style.cursor = '';
          if (textEl) textEl.textContent = 'ADD TO CART';
        } else {
          btn.disabled = true;
          btn.style.opacity = '0.5';
          btn.style.cursor = 'not-allowed';
          if (textEl) textEl.textContent = 'SOLD OUT';
        }
      }

      // Update URL
      if (window.history?.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    };

    for (const picker of pickers) {
      picker.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"]')) {
          const selected = getSelectedOptions(picker);
          updatePickerUI(picker, selected);
          syncPickers(picker);
        }
      });
    }
  }

  // ─── 8. Hero Carousel ────────────────────────────────────────────
  #initHeroCarousel() {
    const carousel = this.refs.heroCarousel;
    const dotsContainer = this.refs.carouselDots;
    const prevBtn = this.refs.carouselPrev;
    const nextBtn = this.refs.carouselNext;
    if (!carousel || !dotsContainer) return;

    const slides = carousel.querySelectorAll('.hg-carousel__slide');
    const dots = dotsContainer.querySelectorAll('.hg-dot');
    let currentIndex = 0;
    let autoPlayInterval;

    const goToSlide = (index) => {
      // Handle wrapping
      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;

      currentIndex = index;
      carousel.style.transform = `translateX(-${index * 100}%)`;

      // Update dots
      dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === index);
      });
    };

    const nextSlide = () => goToSlide(currentIndex + 1);
    const prevSlide = () => goToSlide(currentIndex - 1);

    const startAutoPlay = () => {
      stopAutoPlay();
      autoPlayInterval = setInterval(nextSlide, 5000);
    };

    const stopAutoPlay = () => {
      if (autoPlayInterval) clearInterval(autoPlayInterval);
    };

    // Dot clicks
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        goToSlide(index);
        startAutoPlay(); // Reset timer on manual click
      });
    });

    // Arrow clicks
    prevBtn?.addEventListener('click', () => {
      prevSlide();
      startAutoPlay();
    });

    nextBtn?.addEventListener('click', () => {
      nextSlide();
      startAutoPlay();
    });

    // Pause on hover
    carousel.addEventListener('mouseenter', stopAutoPlay);
    carousel.addEventListener('mouseleave', startAutoPlay);

    // Initial start
    startAutoPlay();
  }

  // ─── 9. WhatsApp Analytics ────────────────────────────────────────
  #initAnalytics() {
    this.addEventListener('click', (e) => {
      const link = e.target.closest('.hg-btn--whatsapp');
      if (!link) return;

      if (typeof gtag === 'function') {
        gtag('event', 'whatsapp_inquiry', {
          event_category: 'conversion',
          event_label: link.dataset.context ?? 'general',
          transport_type: 'beacon',
        });
      }
    });
  }
}

customElements.define('haley-gentleman-component', HaleyGentlemanComponent);
